<template>
    <v-card
        ref="elCard"
        class="garment-size-chart fill-height"
        :class="{ 'in-modal': inModal }"
        v-resize="onResize"
        flat
    >
        <v-card-title class="d-block pa-0">
            <v-toolbar color="secondary" density="compact" flat>
                <v-toolbar-title
                    class="text-title-large"
                >
                    Size Chart
                </v-toolbar-title>
                <v-switch
                    color="accent"
                    class="mr-4 flex-grow-0"
                    v-model="isMetric"
                    label="Metric"
                    inset
                    hide-details
                ></v-switch>
                <v-btn
                    v-if="inModal"
                    @click="onExit()"
                    :icon="mdiWindowClose"
                ></v-btn>
            </v-toolbar>
        </v-card-title>
        <v-card-text>
            <v-table
                class="w-100 text-body-large"
                density="compact"
                :height="tableHeight"
                fixed-header
            >
                <thead>
                    <tr>
                        <th>Size</th>
                        <th>Height</th>
                        <th>Weight</th>
                        <th>Chest</th>
                        <th>Waist</th>
                        <th>Hip</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="size in sizes"
                        :key="`garment-size-${size.uid}`"
                    >
                        <th>{{ size.name }}</th>
                        <td>
                            <MeasurementToggleValue
                                v-model="isMetric"
                                :imperial="size.heightFmt"
                                :metric="size.heightMetricFmt"
                            ></MeasurementToggleValue>
                        </td>
                        <td>
                            <MeasurementToggleValue
                                v-model="isMetric"
                                :imperial="size.weightFmt"
                                :metric="size.weightMetricFmt"
                            ></MeasurementToggleValue>
                        </td>
                        <td>
                            <MeasurementToggleValue
                                v-model="isMetric"
                                :imperial="size.chestGirthFmt"
                                :metric="size.chestGirthMetricFmt"
                            ></MeasurementToggleValue>
                        </td>
                        <td>
                            <MeasurementToggleValue
                                v-model="isMetric"
                                :imperial="size.waistGirthFmt"
                                :metric="size.waistGirthMetricFmt"
                            ></MeasurementToggleValue>
                        </td>
                        <td>
                            <MeasurementToggleValue
                                v-model="isMetric"
                                :imperial="size.hipGirthFmt"
                                :metric="size.hipGirthMetricFmt"
                            ></MeasurementToggleValue>
                        </td>
                    </tr>
                </tbody>
            </v-table>
        </v-card-text>
    </v-card>
</template>

<script lang="ts" setup>
    import type { GarmentSize } from '~/types';
    import { mdiWindowClose } from '@mdi/js';
    import type { VCard, VCardText } from 'vuetify/components';
    import MeasurementToggleValue from '~/components/MeasurementToggleValue/MeasurementToggleValue.vue';

    defineProps<{
        sizes: GarmentSize[];
        inModal: boolean;
    }>();

    const emit = defineEmits<{
        'on-exit': [value: void];
    }>();

    const elCard = shallowRef<InstanceType<typeof VCard>>();
    const isMetric = ref<boolean>(false);
    const tableHeight = ref<string | number>('100%');

    function onExit() {
        emit('on-exit');
    }

    function onResize(): void {
        const $el = elCard.value?.$el;
    
        if ($el) {
            // Toolbar + Content Pad + Extra Pad
            const offset = 48 + 32 + 12;
            tableHeight.value = $el.clientHeight - offset;
        }
    }

    onMounted(() => {
        onResize();
    });
</script>

<style lang="scss">
.garment-size-chart {
    table {
        thead {
            th {
                text-align: start;
                font-size: 110%;
                padding: 0 0 0.4rem 0;
                font-weight: bold;
            }
        }

        tbody {
            th {
                font-weight: medium;
                text-align: start;
                white-space: nowrap;
            }

            div {
                white-space: nowrap;
            }
        }
    }
}
</style>
